import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2, AlertCircle, CheckCircle } from 'lucide-react';

interface ServiceRequestDetail {
  id: string;
  service_category: string;
  property_type: string;
  service_type: string;
  contact_email: string;
  contact_phone: string;
  contact_name: string;
  company?: string;
  details: Record<string, any>;
  status: 'pending' | 'quoted' | 'accepted' | 'rejected' | 'paid';
  quoted_price?: number;
  provider_id?: number;
  payment_status?: string;
  created_at: string;
  updated_at: string;
  assigned_to?: string;
  notes?: string;
}

export default function AdminServiceRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [request, setRequest] = useState<ServiceRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quotedPrice, setQuotedPrice] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchServiceRequest();
  }, [id]);

  const fetchServiceRequest = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.request(`/service-requests/${id}`, 'GET');

      if (response.success) {
        setRequest(response.data);
        setQuotedPrice(response.data.quoted_price?.toString() || '');
        setNotes(response.data.notes || '');
      } else {
        setError(response.message || 'Failed to load service request');
      }
    } catch (err: any) {
      console.error('Error fetching service request:', err);
      setError(err.message || 'An error occurred while loading the request');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrice = async () => {
    if (!id || !quotedPrice) {
      toast({
        title: 'Error',
        description: 'Please enter a valid price',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      const response = await apiService.request(`/service-requests/${id}`, 'PATCH', {
        quoted_price: parseFloat(quotedPrice),
        notes
      });

      if (response.success) {
        setRequest(response.data);
        toast({
          title: 'Success',
          description: 'Service request updated successfully'
        });
      } else {
        toast({
          title: 'Error',
          description: response.message || 'Failed to update request',
          variant: 'destructive'
        });
      }
    } catch (err: any) {
      console.error('Error updating service request:', err);
      toast({
        title: 'Error',
        description: 'Failed to update service request',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'quoted':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'paid':
        return 'bg-emerald-100 text-emerald-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price?: number | string) => {
    if (!price) return 'Not quoted';
    return '£' + parseFloat(String(price)).toFixed(2);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !request) {
    return (
      <AdminLayout>
        <Helmet>
          <title>Error - Service Request | Virewire</title>
        </Helmet>
        <div className="p-6">
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate('/admin/service-requests')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Service Requests
          </Button>

          <Card className="border-l-4 border-l-red-500 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-bold text-red-900 mb-2">Error</h2>
                  <p className="text-red-800">{error || 'Service request not found'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>Service Request {request?.id} - Admin | Virewire</title>
      </Helmet>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/service-requests')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Service Requests
          </Button>
        </div>

        {/* Status Banner */}
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Service Request</h1>
                <p className="text-blue-700 font-mono text-sm">{request.id}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge className={getStatusColor(request.status)}>
                  {request.status?.charAt(0).toUpperCase() + request.status?.slice(1)}
                </Badge>
                {request.status === 'paid' && (
                  <div className="flex items-center gap-1 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-semibold">Paid</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
                    <p className="text-lg text-gray-900">{request.contact_name || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                    <p className="text-lg text-gray-900 break-all">{request.contact_email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Phone</label>
                    <p className="text-lg text-gray-900">{request.contact_phone || 'Not provided'}</p>
                  </div>
                  {request.company && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Company</label>
                      <p className="text-lg text-gray-900">{request.company}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Service Details */}
            <Card>
              <CardHeader>
                <CardTitle>Service Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Category</label>
                    <p className="text-lg text-gray-900">{request.service_category || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Property Type</label>
                    <p className="text-lg text-gray-900 capitalize">{request.property_type?.replace('-', ' ') || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Service Type</label>
                    <p className="text-lg text-gray-900 capitalize">{request.service_type?.replace('-', ' ') || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Details */}
            {request.details && Object.keys(request.details).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Additional Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(request.details).map(([key, value]) => (
                      <div key={key}>
                        <label className="block text-sm font-medium text-gray-600 mb-1 capitalize">
                          {key.replace(/_/g, ' ')}
                        </label>
                        <p className="text-gray-900">
                          {Array.isArray(value) ? value.join(', ') : String(value)}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle>Timeline</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-xs font-medium text-gray-600 min-w-fit">Created:</span>
                  <span className="text-sm text-gray-900">{formatDate(request.created_at)}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-xs font-medium text-gray-600 min-w-fit">Updated:</span>
                  <span className="text-sm text-gray-900">{formatDate(request.updated_at)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Status Info */}
            <Card className={getStatusColor(request.status)}>
              <CardContent className="pt-6">
                <p className="text-sm font-semibold">Status: {request.status?.toUpperCase()}</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Notes Section */}
        <Card>
          <CardHeader>
            <CardTitle>Admin Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add internal notes about this service request..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
